import React, { useContext, useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import {
    HomeIcon,
    UserGroupIcon,
    ArrowRightOnRectangleIcon,
    BriefcaseIcon,
    UserCircleIcon,
    Cog6ToothIcon,
    ChartBarIcon,
    UserIcon,
    CalendarDaysIcon,
    Bars3Icon,
    XMarkIcon,
    WalletIcon,
    BookOpenIcon,
    ArrowsPointingOutIcon,
    ArrowsPointingInIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const Layout = ({ children }) => {
    const { user, logout } = useContext(AuthContext);
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // --- USER GUIDE STATES ---
    const [showGuide, setShowGuide] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);
    const [guideSearchTerm, setGuideSearchTerm] = useState('');
    const iframeRef = useRef(null);

    // Helper to close sidebar on mobile when a link is clicked
    const handleNavClick = () => {
        setIsSidebarOpen(false);
    };

    // --- 1. REAL-TIME SEARCH (Type as you go) ---
    useEffect(() => {
        if (!showGuide || !iframeRef.current) return;

        const iframeWin = iframeRef.current.contentWindow;
        if (!iframeWin) return;

        if (guideSearchTerm.length > 0) {
            // Remove previous selection ranges
            iframeWin.getSelection().removeAllRanges();

            // EXECUTE FIND
            // Note: We do NOT call iframeWin.focus() here, otherwise typing stops.
            // window.find(text, caseSensitive, backwards, wrapAround, wholeWord, searchInFrames, showDialog)
            try {
                iframeWin.find(guideSearchTerm, false, false, true, false, false, false);
            } catch (e) {
                console.error("Search error", e);
            }
        } else {
            iframeWin.getSelection().removeAllRanges();
        }
    }, [guideSearchTerm, showGuide]);

    // --- 2. FIND NEXT (Pressing Enter) ---
    const handleManualSearchSubmit = (e) => {
        e.preventDefault();
        const iframeWin = iframeRef.current?.contentWindow;
        if (iframeWin && guideSearchTerm) {
            // When user hits ENTER, we explicitly find next.
            // We CAN focus here if we want the user to see the blue highlight vividly,
            // but keeping focus on input allows hitting enter repeatedly.
            iframeWin.find(guideSearchTerm, false, false, false, false, false, false);
        }
    };

    // --- 3. CLEAR SEARCH ---
    const clearGuideSearch = () => {
        setGuideSearchTerm('');
        const iframeWin = iframeRef.current?.contentWindow;
        if (iframeWin) {
            iframeWin.getSelection().removeAllRanges();
        }
    };

    const NavItem = ({ to, icon: Icon, label }) => {
        const active = location.pathname === to;
        return (
            <Link
                to={to}
                onClick={handleNavClick}
                className={`
                    group flex items-center px-4 py-3 my-1.5 mx-3 rounded-xl transition-all duration-200 ease-in-out font-medium
                    ${active
                        ? 'bg-gradient-to-r from-indigo-600 to-indigo-600 text-white shadow-lg shadow-indigo-500/30'
                        : 'text-slate-400 hover:bg-white/5 hover:text-white'
                    }
                `}
            >
                <Icon className={`h-5 w-5 mr-3 transition-colors ${active ? 'text-white' : 'text-slate-500 group-hover:text-white'}`} />
                <span className="tracking-wide text-sm">{label}</span>
            </Link>
        );
    };

    return (
        <div className="flex h-screen bg-gray-50 overflow-hidden font-sans">

            {/* --- USER GUIDE MODAL --- */}
            {showGuide && (
                <div className={`fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm animate-fade-in ${isMaximized ? 'p-0' : 'p-4'}`}>
                    <div
                        className={`bg-white flex flex-col overflow-hidden shadow-2xl border border-slate-200 relative transition-all duration-300 ease-in-out ${isMaximized
                            ? 'w-full h-full rounded-none'
                            : 'w-full max-w-5xl h-[85vh] rounded-2xl'
                            }`}
                    >
                        {/* --- MODAL HEADER (Single Row) --- */}
                        <div className="flex items-center gap-3 p-3 border-b border-slate-100 bg-slate-50">

                            {/* TITLE */}
                            <div className="flex-shrink-0 flex items-center text-lg font-bold text-slate-800">
                                <BookOpenIcon className="w-5 h-5 mr-2 text-indigo-600" />
                                <span className="hidden sm:inline">User Guide</span>
                                <span className="sm:hidden">Guide</span>
                            </div>

                            {/* SINGLE SEARCH BAR */}
                            <form onSubmit={handleManualSearchSubmit} className="flex-1 relative group max-w-md mx-auto">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MagnifyingGlassIcon className="h-4 w-4 text-slate-400 group-focus-within:text-indigo-500" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    className="block w-full pl-9 pr-8 py-2 text-sm border border-slate-300 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all shadow-sm"
                                    value={guideSearchTerm}
                                    onChange={(e) => setGuideSearchTerm(e.target.value)}
                                    autoFocus
                                />
                                {guideSearchTerm && (
                                    <button
                                        type="button"
                                        onClick={clearGuideSearch}
                                        className="absolute inset-y-0 right-0 pr-2 flex items-center"
                                    >
                                        <XMarkIcon className="h-4 w-4 text-slate-400 hover:text-rose-500 cursor-pointer" />
                                    </button>
                                )}
                            </form>

                            {/* CONTROLS */}
                            <div className="flex-shrink-0 flex items-center space-x-1">
                                <button
                                    onClick={() => setIsMaximized(!isMaximized)}
                                    className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-500 hover:text-indigo-600"
                                    title={isMaximized ? "Collapse" : "Expand"}
                                >
                                    {isMaximized ? (
                                        <ArrowsPointingInIcon className="w-5 h-5" />
                                    ) : (
                                        <ArrowsPointingOutIcon className="w-5 h-5" />
                                    )}
                                </button>

                                <button
                                    onClick={() => {
                                        setShowGuide(false);
                                        setIsMaximized(false);
                                        setGuideSearchTerm('');
                                    }}
                                    className="p-2 hover:bg-rose-100 hover:text-rose-600 rounded-full transition-colors text-slate-500"
                                    title="Close"
                                >
                                    <XMarkIcon className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        {/* --- IFRAME CONTENT --- */}
                        <div className="flex-1 bg-slate-100 p-0 relative">
                            <iframe
                                ref={iframeRef}
                                src="/userguide.html"
                                title="User Guide"
                                className="w-full h-full border-none"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* --- MOBILE OVERLAY BACKDROP --- */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-gray-900/80 z-30 lg:hidden backdrop-blur-sm transition-opacity"
                    onClick={() => setIsSidebarOpen(false)}
                ></div>
            )}

            {/* --- SIDEBAR --- */}
            <aside
                className={`
                    fixed inset-y-0 left-0 z-40 w-72 bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col shadow-2xl border-r border-slate-700/50 
                    transform transition-transform duration-300 ease-in-out
                    ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
                    lg:translate-x-0 lg:static lg:flex-shrink-0
                `}
            >
                <div className="h-20 sm:h-24 flex items-center justify-between px-6 sm:px-8 border-b border-slate-700/50">
                    <div className="flex items-center space-x-3">
                        <div className="bg-indigo-500/20 p-2 rounded-lg border border-indigo-500/20">
                            <BriefcaseIcon className="h-6 w-6 text-indigo-400" />
                        </div>
                        <div>
                            <span className="block text-xl font-bold text-white tracking-tight">SmartBreak</span>
                            <span className="block text-[10px] uppercase tracking-widest text-slate-400 font-semibold">HR Solutions</span>
                        </div>
                    </div>
                    <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/10">
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                <nav className="flex-1 mt-6 overflow-y-auto px-2 space-y-1 custom-scrollbar flex flex-col">
                    <div className="px-4 mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Main Menu</div>
                    <NavItem to="/dashboard" icon={HomeIcon} label="Dashboard" />
                    <NavItem to="/profile" icon={UserIcon} label="My Profile" />
                    <NavItem to="/calendar" icon={CalendarDaysIcon} label="Leave Calendar" />

                    <div className="px-4 mt-6 mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Analytics</div>
                    <NavItem to="/reports" icon={ChartBarIcon} label="Leave Reports" />
                    {(user?.role === 'Admin' || user?.role === 'Super Admin') && (
                        <>
                            <div className="px-4 mt-6 mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Administration</div>
                            <NavItem to="/admin" icon={UserGroupIcon} label="Employees" />
                            <NavItem to="/admin/balances" icon={WalletIcon} label="Manage Balances" />
                            <NavItem to="/policy" icon={Cog6ToothIcon} label="Policy Settings" />
                        </>
                    )}

                    <div className="flex-1"></div>

                    <div className="px-3 pb-2 mt-4">
                        <button
                            onClick={() => {
                                setShowGuide(true);
                                setIsSidebarOpen(false);
                            }}
                            className="w-full flex items-center px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-600/10 to-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500 hover:text-white transition-all duration-300 group shadow-lg shadow-black/20"
                        >
                            <BookOpenIcon className="h-5 w-5 mr-3 group-hover:animate-pulse" />
                            <span className="text-sm font-bold tracking-wide">User Guide</span>
                        </button>
                    </div>
                </nav>

                <div className="p-4 border-t border-slate-700/50 bg-slate-900/50 backdrop-blur-sm">
                    <div className="flex items-center p-3 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-slate-600 transition-colors">
                        <div className="relative">
                            <UserCircleIcon className="h-10 w-10 text-slate-400" />
                            <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-green-500 border-2 border-slate-800"></span>
                        </div>
                        <div className="ml-3 flex-1 overflow-hidden">
                            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
                            <p className="text-xs text-slate-400 truncate">{user?.role} • {user?.department || 'General'}</p>
                        </div>
                        <button onClick={logout} className="p-2 ml-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-700 transition-colors" title="Logout">
                            <ArrowRightOnRectangleIcon className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </aside>

            {/* --- MAIN CONTENT WRAPPER --- */}
            <div className="flex-1 flex flex-col h-full overflow-hidden relative bg-gray-50">
                <header className="h-16 lg:hidden bg-white border-b border-slate-200 flex items-center justify-between px-4 z-20 shadow-sm flex-shrink-0">
                    <div className="flex items-center">
                        <button onClick={() => setIsSidebarOpen(true)} className="p-2 -ml-2 mr-2 text-slate-600 rounded-lg hover:bg-slate-100">
                            <Bars3Icon className="h-6 w-6" />
                        </button>
                        <span className="font-bold text-lg text-slate-800">SmartBreak</span>
                    </div>
                    <div className="relative"><NotificationBell /></div>
                </header>

                <div className="hidden lg:block absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-indigo-50/50 to-transparent pointer-events-none z-0"></div>
                <div className="hidden lg:block absolute top-6 right-8 z-50">
                    <div className="bg-white/80 backdrop-blur-md p-1 rounded-full shadow-lg border border-white/50 hover:bg-white transition-colors">
                        <NotificationBell />
                    </div>
                </div>

                <main className="flex-1 overflow-y-auto overflow-x-hidden relative z-10 custom-scrollbar">
                    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto min-h-full pb-20">
                        {children}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Layout;